import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CalendarCheck, Loader2 } from "lucide-react";
import { Brand } from "@/components/Brand";
import { bookHiringInterview, getHiringBooking } from "@/lib/api";

// Public: the candidate picks their gym interview time (link from the invite email).
// Slots come from each gym's weekly interview windows in Hiring → Interview slots.
export default function CareersBook() {
  const { token } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState("");
  const [changing, setChanging] = useState(false);

  const load = useCallback(() => getHiringBooking(token).then(setD)
    .catch((e) => setErr(e?.response?.data?.detail || "This page didn't load. Check your connection and refresh.")), [token]);
  useEffect(() => { load(); }, [load]);

  const book = async (s) => {
    setBusy(s.facility_id + s.start); setErr("");
    try { await bookHiringInterview(token, { facility_id: s.facility_id, start: s.start }); setChanging(false); await load(); }
    catch (e) { setErr(e?.response?.data?.detail || "Couldn't book. Try again."); await load(); }
    finally { setBusy(""); }
  };

  if (!d) {
    return <div className="min-h-screen bg-[#0a0a0a] text-zinc-300 flex items-center justify-center p-6 text-center">{err || <Loader2 className="h-6 w-6 animate-spin text-zinc-500" />}</div>;
  }

  const days = {};
  for (const s of d.slots) (days[s.start.slice(0, 10)] ||= []).push(s);

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-zinc-100" data-testid="careers-book">
      <div className="mx-auto max-w-lg px-5 pb-16 space-y-6" style={{ paddingTop: "max(env(safe-area-inset-top), 24px)" }}>
        <Brand showText={false} />
        <h1 className="font-display text-3xl font-extrabold uppercase leading-tight">Your interview day</h1>
        {d.booked && !changing ? (
          <div className="rounded-2xl border border-emerald-500/40 bg-emerald-500/10 p-5 space-y-2" data-testid="careers-booked">
            <CalendarCheck className="h-6 w-6 text-emerald-400" />
            <p className="text-lg">You're booked, {d.first_name}: <b>{d.booked.label}</b> at <b>{d.booked.gym}</b>.</p>
            <p className="text-sm text-zinc-400">Plan for about 2 hours: a short written paper, a practical where you coach a lift and a couple of role plays, then a conversation with the team. Training clothes, water and a pen.</p>
            {d.can_book && <button type="button" onClick={() => setChanging(true)} className="text-sm text-zinc-300 underline">Change the time</button>}
          </div>
        ) : !d.can_book ? (
          <p className="text-zinc-400">Hi {d.first_name}, there's nothing to book on this link right now. Reply to our email and we'll sort it out.</p>
        ) : (
          <>
            <p className="text-zinc-400">Hi {d.first_name}, pick a time that works. Plan for about 2 hours: a short written paper, a practical where you coach a lift and a couple of role plays, then a conversation with the team.</p>
            {err && <p className="text-sm text-amber-300" role="alert">{err}</p>}
            {d.slots.length === 0 && <p className="text-zinc-300">No open times in the next two weeks. Reply to our email or WhatsApp us and we'll find one.</p>}
            {Object.entries(days).map(([day, slots]) => (
              <div key={day} className="space-y-2">
                <div className="text-xs uppercase tracking-[0.18em] text-zinc-500">{slots[0].label.split(",")[0]}</div>
                <div className="grid grid-cols-2 gap-2">
                  {slots.map((s) => (
                    <button key={s.facility_id + s.start} type="button" onClick={() => book(s)} disabled={!!busy}
                      data-testid={`careers-slot-${s.start}`}
                      className="rounded-xl border border-[#2a2a2a] bg-[#111] px-3 py-3 text-left hover:border-rose-500 disabled:opacity-50">
                      <div className="font-semibold">{s.label.split(", ")[1]}</div>
                      <div className="text-xs text-zinc-500">{s.gym}</div>
                      {busy === s.facility_id + s.start && <Loader2 className="h-4 w-4 animate-spin mt-1" />}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
}
